/**
 * Re-demande du mot de passe maitre par entree (« Master password re-prompt »
 * de Bitwarden). Une entree marquee sensible ne montre ni son mot de passe, ni
 * son TOTP, ni ses champs, ni ses notes, et ne s'ouvre pas en edition, tant que
 * le mot de passe maitre n'a pas ete retape -- meme coffre deverrouille, meme
 * poste laisse ouvert une minute.
 *
 * Le drapeau vit DANS les secrets chiffres (`secrets.reprompt === true`) : le
 * serveur ne sait meme pas quelles entrees sont sensibles. La verification
 * rejoue la derivation Argon2id du deverrouillage sur l'enveloppe du compte :
 * rien n'est stocke, rien ne part sur le reseau, la cle obtenue est effacee
 * aussitot. Module pur cote donnees, zero E/S.
 */
import { DecryptionFailedError, unlockVaultAsync, type EntrySecrets } from '@klavox/crypto-core';
import type { AccountEnvelope } from './types.js';

/** L'entree exige-t-elle le mot de passe maitre avant d'etre devoilee ? Seul `true` protege (valeur importee mal formee = non). */
export function isRepromptProtected(secrets: EntrySecrets): boolean {
  return secrets['reprompt'] === true;
}

/**
 * Le mot de passe maitre tape est-il celui du compte ? `false` si faux ; toute
 * AUTRE erreur (enveloppe illisible, Argon2 indisponible) remonte : un refus
 * silencieux la deguiserait en faute de frappe.
 */
export async function verifyMasterPassword(envelope: AccountEnvelope, masterPassword: string): Promise<boolean> {
  return (await masterPasswordHashIfValid(envelope, masterPassword)) !== null;
}

/**
 * Meme verification, qui rend la preuve d'identite pour le serveur (`masterPasswordHashBase64`, derivee, jamais le
 * mot de passe) : `null` si le mot de passe est faux ou vide ; toute autre erreur remonte.
 */
export async function masterPasswordHashIfValid(envelope: AccountEnvelope, masterPassword: string): Promise<string | null> {
  if (masterPassword.length === 0) return null;
  try {
    const unlocked = await unlockVaultAsync({
      masterPassword,
      email: envelope.email,
      kdfParams: envelope.kdfParams,
      protectedSymmetricKey: envelope.protectedSymmetricKey,
    });
    unlocked.vaultKey.destroy();
    return unlocked.masterPasswordHashBase64;
  } catch (cause) {
    if (cause instanceof DecryptionFailedError) return null;
    throw cause;
  }
}
